// Node graphs: a macro drawn as nodes and wires (the Nodes tab, nodeui.ts) instead of a list of steps. Each node's
// output is a selection or a data group, worked out from what is wired into it, every branch on its own: a Box node
// selects its box whatever else is selected, and a Union (+) node joins what its inputs selected. compile turns a graph
// into plain macro steps (macros.ts) for the run as it is: each node's selection is held (a Keep step) under its id
// and put back (Recall, with a mode) where another node takes it; a data group a node makes is a temporary one
// (TEMP_DATA), copied (Data copy) where a node changes it, so its inputs stay as they were. Sinks (Result, Save group,
// Set weight, …) are what a graph does: they run in their order on the canvas (top to bottom, then left to right),
// Result last, and pull in only the nodes they need. A graph macro is saved as a macro (graph, its steps none);
// flatten compiles it where it runs, so the Macros tab, a Macro step and the object page run it as any other.
// A Selection node is the selection the graph starts with (the page's, when it runs on its own).
// A graph is also a sub-graph: Graph input nodes give it a selection (a slot named by <name>; run on its own, or by a
// Macro or For each node, the selection it starts with) or a data group named by one of its variables (<name>), and
// Graph output nodes write a selection (to a slot <name> names) or a data group (to the one <name> names). A Sub Graph
// node runs such a graph: its Graph inputs are the node's inputs, its Result and Graph outputs its outputs (graphPorts),
// its other variables values on the node; selections go by slot (the caller's, read where it is), data groups by
// copies of their own. A Macro or For each node shows its macro's data variables (those only naming data groups, list
// macro or graph) as ports, a data group in and out: wired, the macro works on a copy of what is wired in (For each:
// the same copy each time round, so each round sees what the one before left), and that copy is what comes out.
// Pure: shared with the editor server, which checks a graph as it saves it.
import type { Combine } from './select.ts';
import { type Field, type Params, type Step, type StepKind, INV, KINDS, PHOTO, kindOf, paramValue, varOf, VAR_NAME } from './macros.ts';
import { exprIns, renameIns } from './dataexpr.ts';
import { GROUP_NAME } from './log.ts';

/** What a port carries: a selection (a mask per cloud) or a data group (a number per point). */
export type PortType = 'sel' | 'data';
/** An input: its name on the node, what it takes, and whether the node needs it wired. */
export interface Port { name: string; type: PortType; req: boolean }
/** What a node does: a selection rule, a selection changed, sets joined, a data group made or changed, a macro run, a graph run (Sub Graph), a graph's input, or a sink (does something; no output). */
export type NodeRole = 'rule' | 'unary' | 'combine' | 'data' | 'macro' | 'sub' | 'input' | 'sink';
export interface NodeDef {
  label: string; title: string; cat: string; role: NodeRole;
  ins: Port[]; out: PortType | null; fields: Field[];
  /** It takes as many inputs as are wired, and one more (Union, Intersect). */
  variadic?: boolean;
}

/** A graph's input or output as a Sub Graph node shows it: its variable's name (result: the graph's Result, no variable). */
export interface IoPort { name: string; type: PortType; result?: true }
/** What a graph takes and gives a Sub Graph node: its Graph inputs; its Result and Graph outputs. */
export interface GraphIo { ins: IoPort[]; outs: IoPort[] }

/**
 * A node: its kind, parameters (its fields'), place on the canvas; args: a Macro, For each or Sub Graph node's values
 * for that macro's variables; n: inputs a variadic node shows; ports: a Macro or For each node's macro's data
 * variables; io: a Sub Graph node's graph's inputs and outputs (both kept here, as the editor last saw that macro, so
 * a graph checks and compiles on its own).
 */
export interface GNode { id: string; t: string; p: Params; args?: Params; x: number; y: number; n?: number; ports?: string[]; io?: GraphIo }
/** A wire from output `out` of a node (0, the first, when not given) to input `port` of another. */
export interface Wire { from: string; to: string; port: number; out?: number }
export interface Graph { nodes: GNode[]; wires: Wire[] }

export const MAX_NODES = 200, MAX_WIRES = 400, MAX_INS = 16;
/** A node's id: also a Keep / Recall step's slot (a variable name's characters). */
export const NODE_ID = /^n\d{1,6}$/;
/** The temporary data groups of a run (a data group name: no saved one starts like that); the page doesn't keep them. */
export const TEMP_DATA = '.node:';
export const isTemp = (name: string) => name.startsWith(TEMP_DATA);

/** Selection changes that take a selection in (they work on it). */
const UNARY = new Set<StepKind>(['invert', 'erode', 'dilate', 'widenFrames', 'widenScan']);
/** Steps that do something to the selection given them: sinks. */
const SINKS = new Set<StepKind>(['groupEdit', 'groupSave', 'plane', 'weight', 'weightBlur', 'features', 'calibrate', 'alignCameras', 'mesh']);
/** Steps no node is: Auto tune (its lists are a list macro's), Clear, Data edit (a node's data group is its own), the internal ones, Macro and For each (nodes of their own). */
const NOT_NODES = new Set<string>(['tune', 'clear', 'dataEdit', 'keep', 'recall', 'dataCopy', 'macro', 'blobs']);
/** A graph's input: the selection it starts with, or a data group (its variable's). */
const IN_TYPES: [string, string][] = [['sel', 'selection'], ['data', 'data group']];

/** Data math's ops as a node shows them: A and B (B: what is wired in, or the source picked). */
const DATA_OPS: [string, string][] = [['set', '= B'], ['add', 'A + B'], ['sub', 'A − B'], ['mul', 'A × B'], ['div', 'A ÷ B'], ['min', 'min(A, B)'], ['max', 'max(A, B)']];
const sel = (name: string, req = true): Port => ({ name, type: 'sel', req });
const data = (name: string, req = true): Port => ({ name, type: 'data', req });

let defs: Record<string, NodeDef> | null = null;
/** Every kind of node, by its t (a step kind's own name where it is one). Built when first asked for (macros.ts imports this module). */
export function nodeKinds(): Record<string, NodeDef> {
  if (defs) return defs;
  const d: Record<string, NodeDef> = {};
  for (const [t, k0] of Object.entries(KINDS)) {
    const k = k0 as { label: string; title: string; combines: boolean; fields: Field[] };
    if (NOT_NODES.has(t)) continue;
    const st = t as StepKind;
    if (t === 'dataBand') d[t] = { label: k.label, title: `${k.title}; wired, of the data group wired in (else the one named); with a selection wired in, of those points`, cat: 'Data', role: 'rule', ins: [data('data', false), sel('of', false)], out: 'sel', fields: k.fields };
    else if (k.combines) d[t] = { label: k.label, title: `${k.title}; with a selection wired in, of those points`, cat: PHOTO.has(st) ? 'Photo' : 'Select', role: 'rule', ins: [sel('of', false)], out: 'sel', fields: k.fields };
    else if (UNARY.has(st)) d[t] = { label: k.label, title: k.title, cat: 'Change', role: 'unary', ins: [sel('in')], out: 'sel', fields: k.fields };
    else if (t === 'dataNew' || t === 'planeDetect') d[t] = { label: k.label, title: `${k.title} (the points wired in)`, cat: 'Data', role: 'data', ins: [sel('points')], out: 'data', fields: k.fields.filter(f => f.key !== 'name') };
    else if (t === 'dataOp') d[t] = { label: 'Data math', title: 'a data group\'s values (A\'s points) changed by B\'s (B\'s default where a point isn\'t in B) or, with nothing wired to B, by the source picked; the selection wired in is the points near which a nearness source measures, and with "selected points only" the points that change', cat: 'Data', role: 'data', ins: [data('A'), data('B', false), sel('sel', false)], out: 'data', fields: k.fields.filter(f => f.key !== 'name').map(f => (f.key === 'op' ? { ...f, options: DATA_OPS } : f)) };
    else if (t === 'dataEval') d[t] = { label: 'Data eval', title: `${k.title.replace('the selected points', 'the points wired in').replace(/\. With \+=.*$/, '')}. Its inputs are the data groups the expression reads, one for each name (an input left unwired: the data group of that name)`, cat: 'Data', role: 'data', ins: [sel('points', false)], out: 'data', fields: k.fields.filter(f => f.key === 'expr') };
    else if (t === 'groupSave') d[t] = { label: 'Save group', title: 'write the selection wired in to the scan as a saved group (replacing what that group holds; its points leave the other saved groups and memory groups)', cat: 'Output', role: 'sink', ins: [sel('sel')], out: null, fields: [{ k: 'group', key: 'as', label: 'as', dflt: 'group' }] };
    else if (SINKS.has(st)) d[t] = { label: k.label, title: `${k.title} (the selection wired in)`, cat: PHOTO.has(st) ? 'Photo' : 'Output', role: 'sink', ins: [sel('sel', !PHOTO.has(st))], out: null, fields: k.fields };
    // next to All shown in the Select list: a Graph input's selection without making the graph a sub-graph
    if (t === 'all') d.selection = { label: 'Selection', title: 'the points selected when the graph starts: run from the Macros tab, a pin or a key, the selection as it is then; run by a Macro or For each node, what is wired into it; by a Sub Graph node, nothing (a Graph input gives it one)', cat: 'Select', role: 'input', ins: [], out: 'sel', fields: [] };
  }
  d.union = { label: 'Union (+)', title: 'the points selected by any input', cat: 'Combine', role: 'combine', ins: [sel('a'), sel('b')], out: 'sel', fields: [], variadic: true };
  d.inter = { label: 'Intersect (∩)', title: 'the points selected by every input', cat: 'Combine', role: 'combine', ins: [sel('a'), sel('b')], out: 'sel', fields: [], variadic: true };
  d.sub = { label: 'Subtract (−)', title: 'the points selected by a and not by b', cat: 'Combine', role: 'combine', ins: [sel('a'), sel('b')], out: 'sel', fields: [] };
  d.macro = { label: 'Macro', title: 'run a saved macro (list or nodes) on the selection wired in (none: on nothing selected); its output, what the macro left selected', cat: 'Macro', role: 'macro', ins: [sel('in', false)], out: 'sel', fields: KINDS.macro.fields as Field[] };
  d.blobs = {
    label: 'For each', title: `${KINDS.blobs.title.split(';')[0]}; here the steps inside are a saved macro's, run each time round (its variable passed to the macro's variable of the same name)`, cat: 'Macro', role: 'macro',
    ins: [sel('in', false)], out: 'sel', fields: [...KINDS.blobs.fields as Field[], { k: 'macro', key: 'body', label: 'run', dflt: '' }],
  };
  d.result = { label: 'Result', title: 'the selection when the graph has run (last, after the other outputs)', cat: 'Output', role: 'sink', ins: [sel('sel')], out: null, fields: [] };
  d.input = { label: 'Graph input', title: 'what the graph is given: a selection, its input <name> on a Sub Graph node running it (run on its own, or by a Macro node, the selection it starts with: what is wired into the Macro node; in For each, each time round\'s points), or the data group its variable <name> names (a Sub Graph, Macro or For each node running it wires one in)', cat: 'Graph', role: 'input', ins: [], out: 'sel', fields: [{ k: 'choice', key: 'type', label: '', dflt: 'sel', options: IN_TYPES }, { k: 'var', key: 'sel', label: 'name', dflt: 'in', on: ['type', ['sel']] }, { k: 'var', key: 'name', label: 'name', dflt: 'data', on: ['type', ['data']] }] };
  d.output = { label: 'Graph output', title: 'give out what is wired in: a selection, output <name> of a Sub Graph node running the graph, or a data group, written to the one its variable <name> names (a Sub Graph, Macro or For each node running the graph gives it out there)', cat: 'Graph', role: 'sink', ins: [data('data')], out: null, fields: [{ k: 'choice', key: 'type', label: '', dflt: 'data', options: IN_TYPES }, { k: 'var', key: 'sel', label: 'name', dflt: 'out', on: ['type', ['sel']] }, { k: 'var', key: 'name', label: 'to', dflt: 'data', on: ['type', ['data']] }] };
  d.subgraph = { label: 'Sub Graph', title: 'run a saved node graph that has Graph inputs or outputs: they are this node\'s inputs, its Result and Graph outputs this node\'s outputs, its other variables values here (an input left unwired: nothing selected, or the graph\'s own data group)', cat: 'Graph', role: 'sub', ins: [], out: null, fields: [{ k: 'macro', key: 'name', label: '', dflt: '', graphs: true }] };
  d.keepData = { label: 'Keep data group', title: 'keep the data group wired in under a name (the page keeps it after the run, as a Data group step\'s)', cat: 'Output', role: 'sink', ins: [data('data')], out: null, fields: [{ k: 'data', key: 'name', label: 'as', dflt: 'data' }] };
  return (defs = d);
}

export const nodeDef = (t: string): NodeDef | undefined => (Object.hasOwn(nodeKinds(), t) ? nodeKinds()[t] : undefined);

/** A node's inputs as it shows them: a variadic node's as many as n (at least its two); a Macro or For each node's, its data ports after its selection; a Sub Graph node's, its graph's inputs. */
export function insOf(n: GNode): Port[] {
  const d = nodeDef(n.t);
  if (!d) return [];
  if (d.role === 'sub') return (n.io?.ins ?? []).map(p => ({ name: p.name, type: p.type, req: false }));
  if (n.t === 'output') return [n.p.type === 'sel' ? sel(`<${n.p.sel}>`) : data('data')];
  if (d.role === 'macro' || n.t === 'dataEval') return [...d.ins, ...(n.ports ?? []).map(v => data(v, false))];
  if (!d.variadic) return d.ins;
  const k = Math.max(d.ins.length, Math.min(MAX_INS, n.n ?? 0));
  return Array.from({ length: k }, (_, i) => sel(String.fromCharCode(97 + i)));
}

/** A node's outputs: its one (a Graph input's, the type picked), a Macro or For each node's data ports after it, a Sub Graph node's its graph's. */
export function outsOf(n: GNode): Port[] {
  const d = nodeDef(n.t);
  if (!d) return [];
  if (n.t === 'input') return [n.p.type === 'data' ? data(`<${n.p.name}>`) : sel(`<${n.p.sel}>`)];
  if (d.role === 'sub') return (n.io?.outs ?? []).map(p => ({ name: p.result ? 'result' : p.name, type: p.type, req: false }));
  const base = d.out ? [{ name: 'out', type: d.out, req: false }] : [];
  return d.role === 'macro' ? [...base, ...(n.ports ?? []).map(v => data(v, false))] : base;
}

/** A Data eval node's inputs as its expression has them: the data groups it reads (null: it can't be read; keep what it had). */
export function evalPorts(n: GNode): string[] | null {
  const ps = exprIns(String(n.p.expr));
  return ps && ps.slice(0, MAX_INS - 1);
}

/** A new node of kind t at x, y: its fields' defaults (from: values to start from where they fit). */
export function newNode(t: string, id: string, x: number, y: number, from: Params = {}): GNode {
  const d = nodeDef(t);
  if (!d) throw new Error(`unknown node ${t}`);
  const p: Params = {};
  for (const f of d.fields) p[f.key] = paramValue(f, from[f.key]) ?? f.dflt;
  const n: GNode = { id, t, p, x, y, ...(d.variadic ? { n: d.ins.length } : {}) };
  const ps = t === 'dataEval' ? evalPorts(n) : null;
  if (ps?.length) n.ports = ps;
  return n;
}

/** An id for a new node in g. */
export function nextId(g: Graph): string {
  let n = 0;
  for (const x of g.nodes) n = Math.max(n, +x.id.slice(1));
  return `n${n + 1}`;
}

/** A checked copy of a macro's graph; throws on anything it can't take. */
export function parseGraph(raw: unknown): Graph {
  const r = raw as { nodes?: unknown; wires?: unknown } | null;
  if (!r || typeof r !== 'object' || !Array.isArray(r.nodes) || !Array.isArray(r.wires)) throw new Error('nodes: want {nodes, wires}');
  if (r.nodes.length > MAX_NODES) throw new Error(`nodes: ${MAX_NODES} at most`);
  if (r.wires.length > MAX_WIRES) throw new Error(`nodes: ${MAX_WIRES} wires at most`);
  const ids = new Set<string>();
  const nodes = r.nodes.map((x0): GNode => {
    const x = x0 as Partial<GNode> | null;
    const id = typeof x?.id === 'string' ? x.id : '';
    if (!NODE_ID.test(id) || ids.has(id)) throw new Error(`nodes: bad or repeated id ${JSON.stringify(x?.id)}`);
    ids.add(id);
    const d = typeof x!.t === 'string' ? nodeDef(x!.t) : undefined;
    if (!d) throw new Error(`node ${id}: unknown kind ${JSON.stringify(x!.t)}`);
    const rp = (x!.p ?? {}) as Record<string, unknown>, p: Params = {};
    for (const f of d.fields) {
      // missing: an invert's false, a Graph input's or output's field from before it had it its default
      const v = paramValue(f, rp[f.key] !== undefined ? rp[f.key] : f === INV ? false : x!.t === 'input' || x!.t === 'output' ? f.dflt : undefined);
      if (v === undefined) throw new Error(`node ${id} (${d.label}): bad ${f.key} ${JSON.stringify(rp[f.key])}`);
      p[f.key] = v;
    }
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : 0);
    const out: GNode = { id, t: x!.t!, p, x: num(x!.x), y: num(x!.y) };
    if (d.variadic) out.n = Math.max(d.ins.length, Math.min(MAX_INS, num(x!.n)));
    if ((x!.t === 'macro' || x!.t === 'blobs') && x!.args !== undefined) {
      const a = x!.args as Record<string, unknown>;
      if (!a || typeof a !== 'object' || Array.isArray(a)) throw new Error(`node ${id}: bad args`);
      const args: Params = {};
      for (const [k, v] of Object.entries(a)) {
        if (!VAR_NAME.test(k) || !(typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && v.length <= 200))) throw new Error(`node ${id}: bad value for ${k}`);
        args[k] = v as number | string | boolean;
      }
      if (Object.keys(args).length) out.args = args;
    }
    if (d.role === 'sub' && x!.io !== undefined) {
      const io = x!.io as { ins?: unknown; outs?: unknown } | null;
      const side = (a: unknown, outs: boolean): IoPort[] => {
        if (!Array.isArray(a) || a.length > MAX_INS) throw new Error(`node ${id}: bad io`);
        return a.map(q0 => {
          const q = q0 as Partial<IoPort> | null;
          if (!q || typeof q.name !== 'string' || !VAR_NAME.test(q.name) || (q.type !== 'sel' && q.type !== 'data') || (q.result !== undefined && !(outs && q.result === true && q.type === 'sel'))) throw new Error(`node ${id}: bad io`);
          return { name: q.name, type: q.type, ...(q.result ? { result: true as const } : {}) };
        });
      };
      const ins = side(io?.ins, false), outs = side(io?.outs, true);
      if (ins.length || outs.length) out.io = { ins, outs };
    }
    if ((d.role === 'macro' || x!.t === 'dataEval') && x!.ports !== undefined) {
      const ps = x!.ports as unknown, ok = x!.t === 'dataEval' ? GROUP_NAME : VAR_NAME;   // Data eval's: the data groups its expression reads
      if (!Array.isArray(ps) || ps.length >= MAX_INS || ps.some(v => typeof v !== 'string' || !ok.test(v)) || new Set(ps).size !== ps.length) throw new Error(`node ${id}: bad ports`);
      if (ps.length) out.ports = [...ps] as string[];
    }
    return out;
  });
  const by = new Map(nodes.map(n => [n.id, n])), taken = new Set<string>();
  const wires = r.wires.map((w0): Wire => {
    const w = w0 as Partial<Wire> | null, a = by.get(String(w?.from)), b = by.get(String(w?.to));
    if (!a || !b) throw new Error(`nodes: a wire from ${JSON.stringify(w?.from)} to ${JSON.stringify(w?.to)}: no such node`);
    const k = w!.out === undefined ? 0 : w!.out, outs = outsOf(a), ins = insOf(b), port = typeof w!.port === 'number' ? w!.port : -1;
    if (!Number.isInteger(k) || k < 0 || k >= Math.max(1, outs.length)) throw new Error(`nodes: ${nodeDef(a.t)!.label} (${a.id}) has no output ${+k + 1}`);
    const out = outs[k]?.type;
    if (!out) throw new Error(`nodes: ${nodeDef(a.t)!.label} (${a.id}) has no output`);
    if (!Number.isInteger(port) || port < 0 || port >= ins.length) throw new Error(`nodes: ${nodeDef(b.t)!.label} (${b.id}) has no input ${port + 1}`);
    if (ins[port].type !== out) throw new Error(`nodes: ${nodeDef(a.t)!.label} (${a.id}) gives a ${typeName(out)}, ${nodeDef(b.t)!.label} (${b.id}) input ${ins[port].name} takes a ${typeName(ins[port].type)}`);
    const key = `${b.id}:${port}`;
    if (taken.has(key)) throw new Error(`nodes: two wires into ${nodeDef(b.t)!.label} (${b.id}) input ${ins[port].name}`);
    taken.add(key);
    return { from: a.id, to: b.id, port, ...(k ? { out: k } : {}) };
  });
  return { nodes, wires };
}

export const typeName = (t: PortType) => (t === 'sel' ? 'selection' : 'data group');

/** "Box (n3)": a node, for messages. */
export const nodeName = (n: GNode) => `${nodeDef(n.t)?.label ?? n.t} (${n.id})`;

/** The sinks of g in the order they run: by place on the canvas (top to bottom, then left to right), Result last. */
export function sinkOrder(g: Graph): GNode[] {
  return g.nodes.filter(n => nodeDef(n.t)?.role === 'sink')
    .sort((a, b) => +(a.t === 'result') - +(b.t === 'result') || a.y - b.y || a.x - b.x);
}

/** g has Graph inputs or outputs: a Sub Graph node can run it. */
export const hasIo = (g: Graph) => g.nodes.some(n => n.t === 'input' || n.t === 'output');

/** A Graph input's or output's variable: its selection's (slot) or its data group's. */
const ioName = (n: GNode) => String(n.p.type === 'sel' ? n.p.sel : n.p.name);

/**
 * What g takes and gives as a Sub Graph node runs it, in their place on the canvas: its Graph inputs (one port a name),
 * its Result then its Graph outputs. Throws, naming the node, on two outputs of one name, a name for a selection and a
 * data group, or a selection in and out of one name (the output would write over what the caller wired in).
 */
export function graphPorts(g: Graph): GraphIo {
  const at = (a: GNode, b: GNode) => a.y - b.y || a.x - b.x;
  const ins: IoPort[] = [], outs: IoPort[] = [], types = new Map<string, PortType>();
  const typed = (n: GNode, name: string, t: PortType) => {
    const was = types.get(name);
    if (was && was !== t) throw new Error(`${nodeName(n)}: <${name}> names a ${typeName(t)} here and a ${typeName(was)} elsewhere`);
    types.set(name, t);
  };
  for (const n of g.nodes.filter(n => n.t === 'input').sort(at)) {
    const name = ioName(n), t: PortType = n.p.type === 'data' ? 'data' : 'sel';
    typed(n, name, t);
    if (!ins.some(p => p.name === name)) ins.push({ name, type: t });
  }
  if (g.nodes.some(n => n.t === 'result')) outs.push({ name: 'result', type: 'sel', result: true });
  for (const n of g.nodes.filter(n => n.t === 'output').sort(at)) {
    const name = ioName(n), t: PortType = n.p.type === 'sel' ? 'sel' : 'data';
    typed(n, name, t);
    if (outs.some(p => !p.result && p.name === name)) throw new Error(`${nodeName(n)}: another Graph output gives out <${name}> too`);
    if (t === 'sel' && ins.some(p => p.name === name)) throw new Error(`${nodeName(n)}: <${name}> is a selection input too (an output gives its own)`);
    outs.push({ name, type: t });
  }
  if (ins.length > MAX_INS || outs.length > MAX_INS) throw new Error(`${MAX_INS} graph inputs and outputs a side at most`);
  return { ins, outs };
}

/** The steps a graph's node stands for, for what doesn't need it compiled (variables, the macros it runs): its step with its parameters, a For each's body a Macro step. */
export function nodeSteps(g: Graph): Step[] {
  const out: Step[] = [];
  for (const n of g.nodes) {
    // a Graph input or output: its variable names a data group (the graph's own default when it runs on its own)
    if (n.t === 'input' && n.p.type === 'data') out.push({ t: 'dataCopy', p: { name: 'x', of: `<${n.p.name}>` } });
    if (n.t === 'output' && n.p.type !== 'sel') out.push({ t: 'dataCopy', p: { name: `<${n.p.name}>`, of: 'x' } });
    if (n.t === 'subgraph') out.push({ t: 'macro', p: { name: n.p.name }, ...(n.args ? { args: n.args } : {}) });
    if (!kindOf(n.t)) continue;
    if (n.t === 'blobs') { const { body: _, ...p } = n.p; out.push({ t: 'blobs', p, steps: [callOf(n)] }); }
    else if (n.t === 'dataEval') out.push({ t: 'dataEval', p: { name: 'x', op: '=', expr: n.p.expr } });
    else if (n.t === 'groupSave') out.push({ t: 'groupSave', p: { name: 'group', as: n.p.as } });
    else out.push({ t: n.t as StepKind, p: n.p, ...(n.t === 'macro' && n.args ? { args: n.args } : {}) });
  }
  return out;
}

/** The Macro step a Macro node runs, or a For each node each time round (its variable passed to the macro's of that name); pass: its wired data ports' groups. */
function callOf(n: GNode, pass: Params = {}): Step {
  const args = { ...n.args, ...pass };
  if (n.t !== 'blobs') return { t: 'macro', p: { name: n.p.name }, ...(Object.keys(args).length ? { args } : {}) };
  const v = String(n.p.var);
  return { t: 'macro', p: { name: n.p.body }, args: { [v]: `<${v}>`, ...args } };
}

/** The names of the macros a graph runs (Macro and Sub Graph nodes, For each bodies). */
export const graphRuns = (g: Graph): string[] => g.nodes.flatMap(n => (n.t === 'macro' || n.t === 'subgraph' ? [String(n.p.name)] : n.t === 'blobs' ? [String(n.p.body)] : []));

/** A graph as steps: the steps (in order), and the node each came from. */
export interface Compiled { steps: Step[]; node: string[] }

/**
 * The steps that work out g's sinks in order, each node once (the first sink needing it), every branch from nothing
 * selected (a Graph input's selection: the one it started with, held first); pre: put before slots and temporary data
 * group names, so a graph run inside another (a Macro node) keeps to its own. With no Result it ends on the selection
 * it started with. Throws, naming the node, on a loop, an input that must be wired and isn't, or no sinks.
 */
export function compile(g: Graph, pre = ''): Compiled {
  const steps: Step[] = [], node: string[] = [], by = new Map(g.nodes.map(n => [n.id, n]));
  /** What is wired into each input of a node: a node's output. */
  type Src = { id: string; out: number };
  const into = new Map<string, (Src | null)[]>();
  for (const n of g.nodes) into.set(n.id, insOf(n).map(() => null));
  for (const w of g.wires) { const a = into.get(w.to); if (a && w.port < a.length) a[w.port] = { id: w.from, out: w.out ?? 0 }; }
  const done = new Set<string>(), busy = new Set<string>(), temps: string[] = [];
  const slot = (id: string) => `${pre}${id}`, temp = (id: string) => `${TEMP_DATA}${pre}${id}`, START = slot('start');
  /** Where a node's output is: a selection's slot, a data group's name (a Graph input's: its variable, put in as it runs; a Sub Graph's: its output's own). */
  const slotOf = (x: Src) => {
    const n = by.get(x.id)!, p = n.t === 'subgraph' ? n.io?.outs[x.out] : undefined;
    return n.t === 'input' ? `<${n.p.sel}>` : n.t === 'selection' ? START : p && !p.result ? slot(`${n.id}_${p.name}`) : slot(x.id);
  };
  const dataOf = (x: Src): string => {
    const n = by.get(x.id)!;
    if (n.t === 'input') return `<${n.p.name}>`;
    if (n.t === 'subgraph') return temp(`${n.id}.${n.io!.outs[x.out].name}`);
    return nodeDef(n.t)!.role === 'macro' ? temp(`${n.id}.${n.ports![x.out - 1]}`) : temp(n.id);
  };
  let at = '';
  const emit = (s: Step) => { steps.push(s); node.push(at); };
  const recall = (x: Src | string, mode: Combine = 'replace') => emit({ t: 'recall', mode, p: { slot: typeof x === 'string' ? x : slotOf(x) } });
  const clear = () => emit({ t: 'clear', p: {} });

  /** Work out node id (and what it takes): its selection held in its slot, or its data group in its temporary name. */
  const need = (id: string) => {
    if (done.has(id)) return;
    const n = by.get(id)!, d = nodeDef(n.t)!;
    if (busy.has(id)) throw new Error(`${nodeName(n)}: its output comes back into it (a loop)`);
    busy.add(id);
    const ins = into.get(id)!, ports = insOf(n);
    ports.forEach((pt, i) => { if (pt.req && !ins[i] && !(d.variadic && ins.some(Boolean))) throw new Error(`${nodeName(n)}: wire something into ${pt.name}`); });
    for (const x of ins) if (x) need(x.id);
    const was = at;
    at = id;
    build(n, d, ins);
    at = was;
    busy.delete(id);
    done.add(id);
  };

  const build = (n: GNode, d: NodeDef, ins: (Src | null)[]) => {
    const keep = () => emit({ t: 'keep', p: { slot: slot(n.id) } });
    const st = n.t as StepKind;
    switch (d.role) {
      case 'input': return;   // its selection held first (START), its data group its variable's; Selection's START
      case 'rule': {
        const [dIn, sIn] = n.t === 'dataBand' ? [ins[0], ins[1]] : [null, ins[0]];
        const p = dIn ? { ...n.p, name: dataOf(dIn) } : n.p;
        if (sIn) { recall(sIn); emit({ t: st, mode: 'inter', p }); } else emit({ t: st, mode: 'replace', p });
        keep();
        return;
      }
      case 'unary': recall(ins[0]!); emit({ t: st, p: n.p }); keep(); return;
      case 'combine': {
        const xs = ins.filter((x): x is Src => !!x);
        if (n.t === 'sub') { recall(ins[0]!); recall(ins[1]!, 'sub'); } else xs.forEach((x, i) => recall(x, i ? (n.t === 'union' ? 'add' : 'inter') : 'replace'));
        keep();
        return;
      }
      case 'macro': {
        // a data port wired (in or out): the macro's variable names a copy of what is wired in, the node's own
        const pass: Params = {};
        (n.ports ?? []).forEach((v, k) => {
          const src = ins[k + 1];
          if (!src && !g.wires.some(w => w.from === n.id && (w.out ?? 0) === k + 1)) return;   // as it was: the group its value names
          const name = temp(`${n.id}.${v}`);
          temps.push(name);
          if (src) emit({ t: 'dataCopy', p: { name, of: dataOf(src) } });
          pass[v] = name;
        });
        if (ins[0]) recall(ins[0]); else clear();
        const call = callOf(n, pass);
        if (n.t === 'blobs') { const { body: _, ...p } = n.p; emit({ t: 'blobs', p, steps: [call] }); } else emit(call);
        keep();
        return;
      }
      case 'sub': {
        // its graph's selection inputs read the slots wired in, its data inputs copies of their own; its outputs land in its own
        const name = String(n.p.name), io = n.io ?? { ins: [], outs: [] }, pass: Params = {};
        if (!name) throw new Error(`${nodeName(n)}: pick the graph to run`);
        if (varOf(name)) throw new Error(`${nodeName(n)}: the graph to run can't be a variable`);
        const own = (v: string) => { const t = temp(`${n.id}.${v}`); temps.push(t); return (pass[v] = t); };
        io.ins.forEach((p, k) => {
          const src = ins[k];
          if (!src) return;   // the graph's own: nothing selected (its start, cleared), its data group's default
          if (p.type === 'sel') pass[p.name] = slotOf(src);
          else emit({ t: 'dataCopy', p: { name: own(p.name), of: dataOf(src) } });
        });
        for (const p of io.outs) {
          if (p.result) continue;
          if (p.type === 'sel') pass[p.name] = slot(`${n.id}_${p.name}`);
          else if (!Object.hasOwn(pass, p.name)) own(p.name);
        }
        clear();
        const args = { ...n.args, ...pass };
        emit({ t: 'macro', p: { name }, ...(Object.keys(args).length ? { args } : {}) });
        keep();
        return;
      }
      case 'data': {
        const name = temp(n.id);
        temps.push(name);
        if (st === 'dataOp') {
          const [a, b, s] = ins;
          if (s) recall(s); else clear();
          emit({ t: 'dataCopy', p: { name, of: dataOf(a!) } });
          emit({ t: 'dataOp', p: { ...n.p, name, ...(b ? { src: 'data', of: dataOf(b) } : {}) } });
        } else if (st === 'dataEval') {
          // the names it reads that are wired: the groups wired in (the rest: the run's own data groups, or measures)
          const [s, ...ds] = ins, to: Record<string, string> = {};
          (n.ports ?? []).forEach((v, k) => { if (ds[k]) to[v] = dataOf(ds[k]!); });
          if (s) recall(s); else clear();
          emit({ t: 'dataEval', p: { name, op: '=', expr: renameIns(String(n.p.expr), to) } });
        } else { recall(ins[0]!); emit({ t: st, p: { ...n.p, name } }); }
        return;
      }
      case 'sink': {
        if (n.t === 'keepData') { emit({ t: 'dataCopy', p: { name: n.p.name, of: dataOf(ins[0]!) } }); return; }
        if (n.t === 'output' && n.p.type === 'sel') { recall(ins[0]!); emit({ t: 'keep', p: { slot: `<${n.p.sel}>` } }); return; }
        if (n.t === 'output') { emit({ t: 'dataCopy', p: { name: `<${n.p.name}>`, of: dataOf(ins[0]!) } }); return; }
        if (ins[0]) recall(ins[0]); else clear();
        if (n.t === 'groupSave') {   // through a memory group of its own (Group save writes a memory group)
          const mem = `${TEMP_DATA}${pre}${n.id}`;
          emit({ t: 'groupEdit', p: { act: 'clear', name: mem } });
          emit({ t: 'groupEdit', p: { act: 'add', name: mem } });
          emit({ t: 'groupSave', p: { name: mem, as: n.p.as } });
        } else if (n.t !== 'result') emit({ t: st, p: n.p });
      }
    }
  };

  const sinks = sinkOrder(g);
  if (!sinks.length) throw new Error('nothing to do: add an output node (Result, Save group, …) and wire into it');
  const result = sinks.some(n => n.t === 'result'), start = !result || g.nodes.some(n => n.t === 'selection' || (n.t === 'input' && n.p.type !== 'data'));
  graphPorts(g);   // its inputs and outputs fit together
  if (start) emit({ t: 'keep', p: { slot: START } });
  for (const s of sinks) need(s.id);
  at = '';
  if (!result) recall(START);   // no Result: the selection it was given
  for (const name of temps) emit({ t: 'dataEdit', p: { act: 'delete', name } });   // only the run had them
  return { steps, node };
}
